/* LUCRÜ™ — © 2026 LUCRÜ. All rights reserved. */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  CheckCircleIcon,
  SparkleIcon,
  ShareNetworkIcon,
  CopyIcon,
  TwitterLogoIcon,
  WhatsappLogoIcon,
  TelegramLogoIcon,
  LinkedinLogoIcon,
  FacebookLogoIcon,
  RedditLogoIcon,
  LightningIcon,
} from "@phosphor-icons/react";
import { api, getOrCreateSession } from "@/lib/api";
import Logo from "@/components/Logo";
import LanguageSwitcher from "@/components/LanguageSwitcher";

const Pricing = () => {
  const sessionId = getOrCreateSession();
  const [plans, setPlans] = useState([]);
  const [cycle, setCycle] = useState("monthly");
  const [loading, setLoading] = useState(null);

  useEffect(() => {
    api.get("/billing/plans").then((r) => setPlans(r.data.plans || [])).catch(() => {});
  }, []);

  const checkout = async (plan) => {
    try {
      setLoading(plan.id);
      const r = await api.post("/billing/checkout", {
        session_id: sessionId,
        lookup_key: plan[cycle].lookup_key,
        origin_url: window.location.origin,
      });
      window.location.href = r.data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Checkout failed");
      setLoading(null);
    }
  };

  const shareLink = (plan) => `${window.location.origin}/subscribe/${plan[cycle].lookup_key}`;

  const copyShare = (plan) => {
    navigator.clipboard.writeText(shareLink(plan));
    toast.success("1-click sign-up link copied");
  };

  const openSocial = (network, plan) => {
    const url = encodeURIComponent(shareLink(plan));
    const text = encodeURIComponent(
      `I'm using LUCRÜ — the AI voice co-host that reads my live chat out loud. 30-day free trial on ${plan.name}:`
    );
    const map = {
      twitter: `https://twitter.com/intent/tweet?text=${text}&url=${url}`,
      whatsapp: `https://wa.me/?text=${text}%20${url}`,
      telegram: `https://t.me/share/url?url=${url}&text=${text}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${url}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${url}`,
      reddit: `https://www.reddit.com/submit?url=${url}&title=${text}`,
    };
    window.open(map[network], "_blank", "noopener,noreferrer,width=640,height=640");
  };

  return (
    <div className="min-h-screen bg-[#05050A] text-white">
      <nav className="relative z-10 flex items-center justify-between px-6 md:px-8 py-5 max-w-7xl mx-auto">
        <Link to="/" className="flex items-center gap-2" data-testid="nav-logo">
          <Logo size={38} withBackground className="glow-primary" />
          <div className="flex flex-col leading-none">
            <span className="font-display font-black text-lg tracking-tight">LUCRÜ</span>
          </div>
        </Link>
        <div className="flex items-center gap-3">
          <LanguageSwitcher />
          <Link to="/dashboard" data-testid="pricing-launch-button">
            <button className="btn-pill btn-ghost text-xs">Open App</button>
          </Link>
        </div>
      </nav>

      <section className="max-w-7xl mx-auto px-6 md:px-8 pt-10 pb-24">
        <div className="text-center max-w-2xl mx-auto">
          <div className="text-xs text-[#00E5FF] uppercase tracking-widest mb-3">Pricing</div>
          <h1 className="font-display text-4xl md:text-5xl font-black tracking-tight mb-4">
            Pick a plan. <span className="text-gradient">Keep streaming.</span>
          </h1>
          <p className="text-white/60">
            Every plan starts with a 30-day free trial. Cancel anytime from your Stripe billing portal.
          </p>

          <div className="inline-flex mt-8 bg-[#0F0F16] border border-[#222233] rounded-full p-1" data-testid="billing-cycle-toggle">
            {[
              { id: "monthly", label: "Monthly" },
              { id: "yearly", label: "Yearly · save 20%" },
            ].map((o) => (
              <button
                key={o.id}
                onClick={() => setCycle(o.id)}
                data-testid={`cycle-${o.id}`}
                className={`px-5 py-2 rounded-full text-xs font-semibold transition-colors ${
                  cycle === o.id ? "bg-gradient-to-r from-[#00E5FF] to-[#39FF14] text-[#05050A]" : "text-white/60 hover:text-white"
                }`}
              >
                {o.label}
              </button>
            ))}
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-5 mt-12">
          {plans.map((p, i) => (
            <motion.div
              key={p.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
              className={`relative glass rounded-2xl p-6 flex flex-col ${
                p.recommended ? "border-2 border-[#39FF14] glow-primary" : ""
              }`}
              data-testid={`plan-card-${p.id}`}
            >
              {p.recommended && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#39FF14] text-[#05050A] text-[10px] font-bold uppercase tracking-wider px-3 py-1 rounded-full">
                  Most popular
                </div>
              )}

              <div className="flex items-center gap-2 mb-2">
                <SparkleIcon size={16} color={p.accent} weight="fill" />
                <span className="font-display font-black text-xl">{p.name}</span>
              </div>
              <p className="text-xs text-white/50 mb-5">{p.tagline}</p>

              <div className="mb-5">
                <span className="font-display text-5xl font-black" style={{ color: p.accent }}>
                  ${p[cycle].price}
                </span>
                <span className="text-sm text-white/50 ml-1">{cycle === "monthly" ? "/mo" : "/yr"}</span>
              </div>

              <ul className="space-y-2.5 mb-6 flex-1">
                {p.features.map((f, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-xs text-white/80">
                    <CheckCircleIcon size={14} color={p.accent} weight="fill" className="mt-0.5 shrink-0" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>

              <button
                onClick={() => checkout(p)}
                disabled={loading === p.id}
                data-testid={`plan-checkout-${p.id}`}
                className={`btn-pill w-full justify-center ${p.recommended ? "btn-primary" : "btn-ghost"} disabled:opacity-50`}
              >
                {loading === p.id ? (
                  "Opening Stripe…"
                ) : (
                  <>
                    <LightningIcon weight="fill" size={14} /> Start · 30 days free
                  </>
                )}
              </button>

              {/* 1-click share */}
              <div className="mt-4 pt-4 border-t border-[#222233]">
                <div className="flex items-center justify-between gap-2">
                  <button
                    onClick={() => copyShare(p)}
                    className="flex items-center gap-1.5 text-[10px] text-white/50 hover:text-[#00E5FF] transition-colors"
                    data-testid={`copy-signup-link-${p.id}`}
                  >
                    <CopyIcon size={12} /> Copy 1-click sign-up link
                  </button>
                  <div className="flex items-center gap-1">
                    {[
                      { id: "twitter", icon: <TwitterLogoIcon size={14} /> },
                      { id: "whatsapp", icon: <WhatsappLogoIcon size={14} /> },
                      { id: "telegram", icon: <TelegramLogoIcon size={14} /> },
                      { id: "facebook", icon: <FacebookLogoIcon size={14} /> },
                      { id: "linkedin", icon: <LinkedinLogoIcon size={14} /> },
                      { id: "reddit", icon: <RedditLogoIcon size={14} /> },
                    ].map((s) => (
                      <button
                        key={s.id}
                        onClick={() => openSocial(s.id, p)}
                        className="w-7 h-7 rounded-full border border-[#222233] flex items-center justify-center text-white/50 hover:text-white hover:border-[#00E5FF] transition-colors"
                        data-testid={`share-${p.id}-${s.id}`}
                        title={`Share on ${s.id}`}
                      >
                        {s.icon}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        <p className="text-center text-[11px] text-white/30 mt-10">
          Payments processed by Stripe for LUCRÜ. Taxes may apply depending on your country.
        </p>
      </section>

      <footer className="border-t border-[#222233] py-6 text-center text-[10px] text-white/40">
        © 2026 LUCRÜ · LUCRÜ™ is a licensed product of LUCRÜ
      </footer>
    </div>
  );
};

export default Pricing;
